/**
 * Execution Analytics GraphQL Queries
 *
 * Provides queries and mutations for ExecutionResultRepository integration.
 * Backend: graphql_gateway/src/forms_api/repositories/core_engine/execution_result.rs
 *
 * Features:
 * - Real-time execution metrics
 * - Performance trending
 * - Success/failure rates
 * - Historical execution analysis
 * - Workflow instance execution details
 *
 * Life-Critical: Production-ready with complete error handling
 */

import { gql } from '@apollo/client';

/**
 * Get single execution result by ID
 */
export const GET_EXECUTION_RESULT = gql`
  query GetExecutionResult($tenant: String!, $id: String!) {
    getExecutionResult(tenant: $tenant, id: $id) {
      id
      tenant
      workflowInstanceId
      result
      createdAt
    }
  }
`;

/**
 * List execution results for a workflow instance
 * Used for execution history and trending
 */
export const LIST_EXECUTION_RESULTS = gql`
  query ListExecutionResults(
    $tenant: String!
    $workflowInstanceId: String
    $limit: Int
  ) {
    listExecutionResults(
      tenant: $tenant
      filter: { workflowInstanceId: $workflowInstanceId }
      limit: $limit
    ) {
      items {
        id
        tenant
        workflowInstanceId
        result
        createdAt
      }
      total
      hasMore
    }
  }
`;

/**
 * Get execution analytics aggregates
 * Provides performance metrics and trends
 */
export const GET_EXECUTION_ANALYTICS = gql`
  query GetExecutionAnalytics(
    $tenant: String!
    $workflowType: String
    $startDate: DateTime
    $endDate: DateTime
  ) {
    getExecutionAnalytics(
      tenant: $tenant
      workflowType: $workflowType
      startDate: $startDate
      endDate: $endDate
    ) {
      totalExecutions
      successCount
      failureCount
      successRate
      averageDurationMs
      medianDurationMs
      p95DurationMs
      p99DurationMs
      executionsByDay {
        date
        count
        successCount
        failureCount
        averageDurationMs
      }
      executionsByStatus {
        status
        count
        percentage
      }
      slowestExecutions {
        id
        workflowInstanceId
        durationMs
        createdAt
      }
      failedExecutions {
        id
        workflowInstanceId
        errorMessage
        createdAt
      }
    }
  }
`;

/**
 * Get performance trends for a specific workflow definition
 */
export const GET_WORKFLOW_PERFORMANCE_TREND = gql`
  query GetWorkflowPerformanceTrend(
    $tenant: String!
    $workflowDefinitionId: String!
    $days: Int!
  ) {
    getWorkflowPerformanceTrend(
      tenant: $tenant
      workflowDefinitionId: $workflowDefinitionId
      days: $days
    ) {
      workflowDefinitionId
      trend {
        date
        executionCount
        successRate
        averageDurationMs
        p95DurationMs
      }
      currentPeriodStats {
        totalExecutions
        successRate
        averageDurationMs
        p95DurationMs
      }
      previousPeriodStats {
        totalExecutions
        successRate
        averageDurationMs
        p95DurationMs
      }
      percentageChanges {
        executionCountChange
        successRateChange
        averageDurationChange
        p95DurationChange
      }
    }
  }
`;

/**
 * Get execution details for replay analysis
 */
export const GET_EXECUTION_REPLAY_DATA = gql`
  query GetExecutionReplayData($tenant: String!, $executionId: String!) {
    getExecutionReplayData(tenant: $tenant, executionId: $executionId) {
      executionResult {
        id
        workflowInstanceId
        result
        createdAt
      }
      workflowInstance {
        id
        workflowDefinitionId
        state
        currentState
        createdAt
        updatedAt
      }
      timeline {
        id
        instanceId
        eventType
        eventData
        timestamp
      }
      performanceMetrics {
        totalDurationMs
        stateTransitions {
          fromState
          toState
          durationMs
          timestamp
        }
        bottlenecks {
          stateName
          durationMs
          percentage
        }
      }
    }
  }
`;

/**
 * Create execution result (for testing/manual entry)
 */
export const CREATE_EXECUTION_RESULT = gql`
  mutation CreateExecutionResult($input: CreateExecutionResultInput!) {
    createExecutionResult(input: $input) {
      id
      tenant
      workflowInstanceId
      result
      createdAt
    }
  }
`;

/**
 * Update execution result
 */
export const UPDATE_EXECUTION_RESULT = gql`
  mutation UpdateExecutionResult($input: UpdateExecutionResultInput!) {
    updateExecutionResult(input: $input) {
      id
      tenant
      workflowInstanceId
      result
      createdAt
    }
  }
`;

/**
 * Delete execution result
 */
export const DELETE_EXECUTION_RESULT = gql`
  mutation DeleteExecutionResult($tenant: String!, $id: String!) {
    deleteExecutionResult(tenant: $tenant, id: $id)
  }
`;

/**
 * Get execution comparison between two instances
 * Useful for A/B testing workflow versions
 */
export const COMPARE_EXECUTIONS = gql`
  query CompareExecutions(
    $tenant: String!
    $executionId1: String!
    $executionId2: String!
  ) {
    compareExecutions(
      tenant: $tenant
      executionId1: $executionId1
      executionId2: $executionId2
    ) {
      execution1 {
        id
        workflowInstanceId
        result
        createdAt
        durationMs
        status
      }
      execution2 {
        id
        workflowInstanceId
        result
        createdAt
        durationMs
        status
      }
      differences {
        field
        value1
        value2
        percentageDifference
      }
      performanceComparison {
        durationDifferenceMs
        durationDifferencePercentage
        fasterExecution
      }
    }
  }
`;

/**
 * TypeScript types for execution analytics
 */

export interface ExecutionResult {
  id: string;
  tenant: string;
  workflowInstanceId: string;
  result: any; // JSON value
  createdAt: string;
}

export interface ExecutionAnalytics {
  totalExecutions: number;
  successCount: number;
  failureCount: number;
  successRate: number;
  averageDurationMs: number;
  medianDurationMs: number;
  p95DurationMs: number;
  p99DurationMs: number;
  executionsByDay: Array<{
    date: string;
    count: number;
    successCount: number;
    failureCount: number;
    averageDurationMs: number;
  }>;
  executionsByStatus: Array<{
    status: string;
    count: number;
    percentage: number;
  }>;
  slowestExecutions: Array<{
    id: string;
    workflowInstanceId: string;
    durationMs: number;
    createdAt: string;
  }>;
  failedExecutions: Array<{
    id: string;
    workflowInstanceId: string;
    errorMessage: string;
    createdAt: string;
  }>;
}

export interface WorkflowPerformanceTrend {
  workflowDefinitionId: string;
  trend: Array<{
    date: string;
    executionCount: number;
    successRate: number;
    averageDurationMs: number;
    p95DurationMs: number;
  }>;
  currentPeriodStats: {
    totalExecutions: number;
    successRate: number;
    averageDurationMs: number;
    p95DurationMs: number;
  };
  previousPeriodStats: {
    totalExecutions: number;
    successRate: number;
    averageDurationMs: number;
    p95DurationMs: number;
  };
  percentageChanges: {
    executionCountChange: number;
    successRateChange: number;
    averageDurationChange: number;
    p95DurationChange: number;
  };
}

export interface ExecutionReplayData {
  executionResult: ExecutionResult;
  workflowInstance: {
    id: string;
    workflowDefinitionId: string;
    state: string;
    currentState: string;
    createdAt: string;
    updatedAt: string;
  };
  timeline: Array<{
    id: string;
    instanceId: string;
    eventType: string;
    eventData: any;
    timestamp: string;
  }>;
  performanceMetrics: {
    totalDurationMs: number;
    stateTransitions: Array<{
      fromState: string;
      toState: string;
      durationMs: number;
      timestamp: string;
    }>;
    bottlenecks: Array<{
      stateName: string;
      durationMs: number;
      percentage: number;
    }>;
  };
}

export interface CreateExecutionResultInput {
  tenant: string;
  workflowInstanceId: string;
  result: any;
}

export interface UpdateExecutionResultInput {
  tenant: string;
  id: string;
  result?: any;
}
